/* eslint-disable no-unused-vars */
import { Button, DialogActions, DialogTitle, IconButton, TextField } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { useCreateDir } from 'apollo/mutations/Gallery';
import React, { SyntheticEvent, useState } from 'react';
import { ExtendedNodeData, TreeItem } from 'react-sortable-tree';
import { addNode } from '../manager/helpers/TreeHelperFunctions';

type TFsFormNode = { dir: string; open: boolean; rowInfo: ExtendedNodeData | null };

interface IFsNodeForm extends TFsFormNode {
    setForm: ({ dir, open, rowInfo }: TFsFormNode) => void;
    treeState: TreeItem[] | null;
    setTreeState: (treeState: TreeItem[] | null) => void;
}

interface IFormData {
    directory?: string;
}

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            '& > *': {
                width: '50%',
            },
        },
        invalidInput: {
            border: 'red solid 2px',
            '&:after': {
                borderBottom: '2px solid red',
            },
        },
        content: {
            padding: 20,
        },
        titleContainer: {
            paddingTop: 20,
            paddingLeft: 20,
            paddingRight: 20,
        },
        errorMessage: {
            color: 'tomato',
            fontSize: '.85rem',
        },
        title: {
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 0,
            paddingRight: 0,
        },
        iconButton: {
            height: 50,
            width: 50,
        },
    }),
);

const AddFSFolderForm = ({ dir, open, rowInfo, setForm, treeState, setTreeState }: IFsNodeForm) => {
    const [formData, setFormData] = useState<IFormData>({});
    const [createDir] = useCreateDir({});
    const [error, setError] = useState<string | null>(null);
    const classes = useStyles();
    const regex = /\s/;

    const OnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const { value, name } = e.currentTarget;
        if (!regex.test(value)) {
            if (error) {
                setError(null);
            }
            setFormData({ [name]: value });
        } else {
            setError('The directory name cannot contain any spaces. Use an underscore instead.');
        }
    };

    const OnSaveHandler = (e: SyntheticEvent) => {
        e.preventDefault();

        const { directory } = formData;

        if (!directory) {
            return setError('There is no directory name filled in');
        }

        if (directory && regex.test(directory)) {
            return setError('Cannot add directory, the name contains spaces');
        }

        createDir({
            variables: {
                parent_dir: dir,
                dir: directory || '/',
            },
        })
            .then(async (res) => {
                const {
                    data: { makeDir },
                } = res;
                if (treeState && rowInfo && res && directory) {
                    // The tree needs to be updated so we don't have to refetch the whole tree and possibly lose changes.
                    const addFolder = await addNode(
                        treeState,
                        { title: directory, children: [], isDirectory: true, expanded: true, id: makeDir.id },
                        rowInfo,
                        rowInfo?.node?.id,
                    );

                    addFolder && setTreeState([...addFolder?.treeData]);
                }

                setForm({ dir: '', open: false, rowInfo: null });
                setFormData({});
            })
            .catch((err) => {
                setError(err.message);
            });
    };

    const handleClose = () => {
        setForm({ dir: '', open: false, rowInfo: null });
        setFormData({});
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            onBackdropClick={handleClose}
            onEscapeKeyDown={handleClose}
            aria-labelledby='form-dialog-title'
        >
            <Grid container className={classes.titleContainer}>
                <Grid item xs={12}>
                    <Grid container justify='space-between'>
                        <DialogTitle className={classes.title} id='form-dialog-title'>
                            Add a new directory
                        </DialogTitle>
                        <IconButton aria-label='close' className={classes.iconButton} onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
            <DialogContent className={classes.content}>
                <DialogContentText>
                    To add a new directory we need some default information about what kind of item this will be. Please
                    fill in the form below and click on add directory.
                </DialogContentText>
                    <form
                        className={classes.root}
                        autoComplete='off'
                        onSubmit={(e: SyntheticEvent) => OnSaveHandler(e)}
                    >
                        <TextField
                            id='standard-basic'
                            label='Directory name'
                            name='directory'
                            inputProps={{ className: error ? classes.invalidInput : '', pattern: /\s/ }}
                            autoFocus
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => OnChangeHandler(e)}
                        />
                    </form>
                {error && <p className={classes.errorMessage}>Error: {error}</p>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='primary'>
                    Cancel
                </Button>
                <Button onClick={OnSaveHandler} color='primary'>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddFSFolderForm;
